import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { formatApiError } from "@/lib/utils-app";
import { toast } from "sonner";

/**
 * Compose a message to the registry admin team. Fans out to every admin's
 * inbox on the backend; the user also gets a confirmation in their inbox.
 */
export default function ContactAdminDialog({ open, onClose, onSent }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setSubject("");
    setBody("");
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const submit = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Subject and message are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/messages/contact-admin", {
        subject: subject.trim(),
        body: body.trim(),
      });
      toast.success("Message sent — an admin will reply to your inbox");
      reset();
      onSent?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-lg" data-testid="contact-admin-dialog">
        <DialogHeader>
          <DialogTitle>Contact admin</DialogTitle>
          <DialogDescription>
            Have a question about registrations, transfers, or billing? Drop
            a note here — the admin team will reply straight back to your
            inbox.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
              Subject *
            </Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={120}
              placeholder="e.g. Kennel verification"
              className="mt-1 rounded-lg"
              data-testid="contact-admin-subject"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
              Message *
            </Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
              maxLength={4000}
              placeholder="Share the details you'd like the admin team to see…"
              className="mt-1 rounded-lg"
              data-testid="contact-admin-body"
            />
            <p className="mt-1 text-[11px] text-[#6B6B6B] text-right">
              {body.length}/4000
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} className="rounded-full">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
            data-testid="contact-admin-submit"
          >
            {submitting ? "Sending…" : "Send to admin"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
